import { NavLink } from "react-router-dom";
import { LayoutDashboard, FileText, FileCheck, Car, Users, Settings, ShieldCheck, LogOut, ClipboardList } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { name: "Tableau de bord", icon: LayoutDashboard, to: "/", testid: "nav-dashboard" },
  { name: "Factures", icon: FileText, to: "/factures", testid: "nav-invoices" },
  { name: "Devis", icon: FileCheck, to: "/devis", testid: "nav-quotes" },
  { name: "Bons de transport", icon: ClipboardList, to: "/bons", testid: "nav-transport" },
  { name: "Clients", icon: Users, to: "/clients", testid: "nav-clients" },
  { name: "Paramètres", icon: Settings, to: "/parametres", testid: "nav-settings" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col fixed inset-y-0 bg-slate-900 text-slate-100 border-r border-slate-800 z-30">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#C5A059] flex items-center justify-center shrink-0">
              <Car className="h-5 w-5 text-slate-900" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-extrabold text-lg leading-none tracking-tight">MB TRANSPORT</div>
              <div className="text-[11px] text-slate-400 mt-1">VTC · Marseille</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#C5A059] text-slate-900"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.name}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-slate-800 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-full bg-[#C5A059] text-slate-900 flex items-center justify-center text-xs font-bold shrink-0">
                {(user?.name || user?.username || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-sm text-white font-medium truncate">{user?.username || "—"}</div>
                <div className="text-[11px] text-slate-400">Administrateur</div>
              </div>
            </div>
            <button onClick={logout} title="Déconnexion" data-testid="logout-btn" className="p-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-400">
            <ShieldCheck className="h-4 w-4 text-[#C5A059]" />
            <span>SIRET 992 510 933 00011</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 min-w-0">
        {/* mobile top bar */}
        <div className="md:hidden sticky top-0 z-20 bg-slate-900 text-white px-4 py-3 flex items-center gap-2 overflow-x-auto">
          <span className="font-display font-bold mr-2">MB TRANSPORT</span>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`${item.testid}-mobile`}
              className={({ isActive }) =>
                `p-2 rounded-lg shrink-0 ${isActive ? "bg-[#C5A059] text-slate-900" : "text-slate-300"}`
              }
            >
              <item.icon className="h-5 w-5" />
            </NavLink>
          ))}
          <button onClick={logout} data-testid="logout-btn-mobile" className="p-2 rounded-lg shrink-0 text-slate-300 ml-auto">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
        <main className="p-5 sm:p-8 max-w-[1400px] mx-auto">{children}</main>
      </div>
    </div>
  );
};

export const StatusBadge = ({ meta }) => (
  <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold border ${meta?.cls || "bg-slate-100 text-slate-700 border-slate-200"}`}>
    {meta?.label || "—"}
  </span>
);

export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
    <div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);
